import Shell from '../components/Shell';
import Seo from '../components/Seo';
import { PageHero, Section, CTA } from '../components/Pieces';
import { techCapabilities } from '../mock';
import { BookOpen, Layers, Video, Cpu, HardDrive, LineChart, IdCard, Wifi } from 'lucide-react';

const techIcon = (id) => ({
  'learning-intelligence': BookOpen,
  'platforms': Layers,
  'content-tlm': Video,
  'classroom-delivery': Cpu,
  'edge': HardDrive,
  'evidence': LineChart,
  'identity': IdCard,
  'secure': Wifi,
}[id] || Layers);

export function Technology() {
  return (
    <Shell>
      <Seo title="Technology"
        description="Mashmari technology organised by capability: learning intelligence, platforms, content and TLM, classroom delivery, edge infrastructure, evidence and secure connectivity." />
      <PageHero
        kicker="Technology Platform"
        title="Technology that disappears into the outcome."
        sub="Organised by capability, not product. Mashmari product names are implementations underneath these capabilities."
        breadcrumbs={[{ label: 'Technology' }]}
      />
      <Section bg="white">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {techCapabilities.map((c) => {
            const Icon = techIcon(c.id);
            return (
              <div key={c.id} className="mm-card p-6">
                <div className="w-12 h-12 rounded-full bg-[var(--mm-cream)] text-[var(--mm-purple)] flex items-center justify-center"><Icon className="w-5 h-5"/></div>
                <h3 className="mt-5 font-display text-lg font-bold text-[var(--mm-ink)]">{c.title}</h3>
                <p className="mt-2 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </Section>
      <CTA title="Explore how our capabilities compose into your programme." />
    </Shell>
  );
}

export function Research() {
  return (
    <Shell>
      <Seo title="Research & Standards"
        description="Standards contributions, reference architectures and applied research across remote classrooms, educational intelligence and secure broadband." />
      <PageHero
        kicker="Research & Standards"
        title="Contributing to standards. Publishing architecture. Shaping the field."
        sub="We distinguish clearly between Published Standard, Standards Contribution, Research / Proposed Architecture and Product Implementation."
        breadcrumbs={[{ label: 'Research & Standards' }]}
      />
      <Section bg="white">
        <div className="grid md:grid-cols-2 gap-5">
          {[
            { t: 'ITU-T Remote Classroom Work', b: 'Contributions to standardisation of remote and autonomous classroom architectures (Y.4234 direction).', tag: 'Standards Contribution' },
            { t: 'Education Technology Architectures', b: 'Reference architectures connecting curriculum intelligence, TLM factories, teacher capability and classroom delivery.', tag: 'Research / Proposed Architecture' },
            { t: 'AI-Enabled Educational Intelligence', b: 'Curriculum ingestion, competency mapping, lesson intelligence and adaptive learning pathways with human-in-the-loop governance.', tag: 'Research / Proposed Architecture' },
            { t: 'Secure Broadband & CPE Intelligence', b: 'Network-level policy enforcement and behavioural visibility for connected devices.', tag: 'Product Implementation' },
          ].map((r) => (
            <div key={r.t} className="mm-card p-6">
              <span className="inline-block text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--mm-purple)] bg-[var(--mm-cream)] rounded-full px-3 py-1">{r.tag}</span>
              <h3 className="mt-4 font-display text-xl font-bold text-[var(--mm-ink)]">{r.t}</h3>
              <p className="mt-2 text-sm text-[var(--mm-ink)]/70 leading-relaxed">{r.b}</p>
            </div>
          ))}
        </div>
      </Section>
      <CTA title="Collaborate on standards, architecture and applied research." />
    </Shell>
  );
}

export function About() {
  return (
    <Shell>
      <Seo title="About"
        description="Mashmari Consultants Private Limited works at the intersection of education, telecom and digital infrastructure — from architecture to field evidence and scale." />
      <PageHero
        kicker="About Mashmari"
        title="Built at the intersection of education, telecom and digital infrastructure."
        sub="An organisation that can move from Problem → Architecture → Product → Pilot → Field Execution → Evidence → Scale."
        breadcrumbs={[{ label: 'About' }]}
      />
      <Section bg="white">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[var(--mm-ink)] mm-rule">Capability, not category.</h2>
            <p className="mt-6 text-[var(--mm-ink)]/70 leading-relaxed">Mashmari combines experience across education transformation, learning technology, telecom, remote-classroom systems, AI-assisted educational workflows, edge/cloud systems, cybersecurity, standards and field implementation.</p>
            <p className="mt-4 text-[var(--mm-ink)]/70 leading-relaxed">We don’t position as an “AI company”. AI is an enabling capability within a larger educational architecture that keeps human review, teacher agency and institutional governance at the centre.</p>
          </div>
          <div className="grid gap-3">
            {['Education transformation','Learning technology','Telecom','Remote classroom systems','AI-assisted educational workflows','Edge / cloud systems','Cybersecurity','Standards','Field implementation'].map((x) => (
              <div key={x} className="bg-[var(--mm-cream)] border border-[var(--mm-line)] rounded-xl px-5 py-3 text-sm font-medium text-[var(--mm-ink)]">• {x}</div>
            ))}
          </div>
        </div>
      </Section>
      <CTA title="Talk to Mashmari." />
    </Shell>
  );
}
